import type { SuperHubRouteParams } from '@routes/SuperHub/types';

import { isVersionName } from '@readme/backend/iso';
import { parseLanguageVersion } from '@readme/i18n';
import { useCallback } from 'react';
import { matchPath } from 'react-router-dom';

import useBuildRoute from '@core/hooks/useBuildRoute';
import { useProjectStore } from '@core/store';
import { useSuperHubContext } from '@routes/SuperHub/components/Context';
import { SuperHubRoutePaths } from '@routes/SuperHub/types';

/**
 * Generates a function that when called returns a URL for the provided branch
 * name that is based off the current route location path and hash. Use this to
 * redirect users from one branch to another. Route slug segments are removed
 * because not every branch will have equivalent pages. So we defer to the
 * section and let users be redirected to the first page of that section.
 * @example
 * ```ts
 * // -> /docs/child-page#/appearance/theme-editor
 * const buildVersionPath = useBuildBranchPath();
 * const redirectToVersionUrl = buildVersionPath('2.0');
 * window.location.assign(redirectToVersionUrl);
 * // -> /v2.0/docs#/appearance/theme-editor
 * ```
 */
export default function useBuildBranchPath() {
  const defaultVersion = useProjectStore(s => s.data.default_version.name);

  const { browserRouterLocation } = useSuperHubContext();
  const { pathname, hash } = browserRouterLocation;
  const match = matchPath<SuperHubRouteParams>(pathname, Object.values(SuperHubRoutePaths));
  const { action: actionSegment, section: sectionSegment } = match?.params ?? {};

  const buildRoute = useBuildRoute();

  return useCallback(
    /**
     * Returns a relative path given the provided branch name that is used to
     * redirect users from one branch to another.
     *
     * @param branchName Bare branch name (no `v` prefix).
     * @param options.hash Override the hash on the resulting URL. Defaults to
     *   the current route hash. Pass `''` to drop the hash entirely — useful
     *   when navigating from an overlay (e.g. `#/branches`) where
     *   you want the overlay to close on click instead of following the user
     *   to the new branch.
     */
    (branchName: string, options?: { hash?: string }) => {
      /**
       * Path construction logic here mirrors the Hub header's branch dropdown
       * @see packages/react/src/Hub/Header/utils/hooks.js
       */
      const branchSegment = defaultVersion !== branchName && `v${encodeURIComponent(branchName)}`;
      let segments = [branchSegment, actionSegment, sectionSegment];

      // Versions can't `review`, so if we're redirecting on the review tab let's go to editing the docs instead
      if (actionSegment === 'review' && isVersionName(branchName.startsWith('v') ? branchName : `v${branchName}`)) {
        segments = [branchSegment, 'update', sectionSegment || 'docs'];
      }

      // We need to extract the language code from the branch name and pass it to `buildRoute` so it can be
      // included in the baseUrl properly when i18n is enabled.
      const { lang } = parseLanguageVersion(branchName);

      return buildRoute({
        includeBaseUrl: true,
        hash: options?.hash ?? hash,
        pathnameSegments: segments,
        language: lang,
      });
    },
    [defaultVersion, buildRoute, hash, actionSegment, sectionSegment],
  );
}
